// @ts-nocheck
import { HandlerWithResult } from "@insite/client-framework/HandlerCreator";
import {
    BatchLoadProductsParameter,
    BatchLoadProductsResult,
    chain,
    ProcessProducts,
} from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import { UploadedItem } from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import { StoreOrderUploadRowError } from "@store/Framework/Store/Store/Components/OrderUpload/StoreOrderUploadState";
import { replaceInChain } from "@toolkit/Modules/client-framework/HandlerCreator";

/* Store Custom Code - Start */
export const enum StoreUploadError {
    None,
    NotEnough,
    ConfigurableProduct,
    StyledProduct,
    Unavailable,
    InvalidUnit,
    NotFound,
    OutOfStock,
    Negative,
    MinimumBiggerMaximum,
    Over25Chars,
    Existed,
    BinNegativeCount,
    BinNotFound,
    WrongType,
    MisMatch,
    ProductRequired,
    CountNotProvided,
    SnDProduct,
}

export interface StoreBatchLoadProductsResult extends Omit<BatchLoadProductsResult, "rowErrors"> {
    rowErrors: StoreOrderUploadRowError[];
}

type HandlerType = HandlerWithResult<BatchLoadProductsParameter, StoreBatchLoadProductsResult>;
/* Store Custom Code - End */
export const StoreProcessProducts: /* Store Custom Code */ HandlerType = props => {
    const state = props.getState();
    const currentPage = getCurrentPage(state);
    const processState =
        ["VmiBinsPage", "VmiLocationDetailsPage"].indexOf(currentPage.type) !== -1
            ? state.components.vmiBinsImportModal.modalIsOpen
                ? state.components.vmiBinsImportModal
                : state.components.vmiCountsImportModal
            : state.components.orderUpload;
    const products = props.result.apiResult;
    if ("uploadCancelled" in processState && processState.uploadCancelled) {
        return false;
    }

    for (let i = 0; i < products.length; i++) {
        const item =
            "uploadedItems" in processState
                ? processState.uploadedItems[i]
                : "parsedItems" in processState
                  ? processState.parsedItems[i]
                  : null;

        if (item === null) {
            continue;
        }

        const index = props.parameter.firstRowHeading ? i + 2 : i + 1;
        const product = products[i];
        if (product) {
            const error = validateProduct(product, props.parameter.checkInventory);
            if (error === StoreUploadError.None /* Store Custom Code */) {
                product.qtyOrdered = !item.qtyOrdered ? 1 : item.qtyOrdered;
                const isInvalidUnitOfMeasure = setProductUnitOfMeasure(product, item, index, props.result.rowErrors);
                if (!isInvalidUnitOfMeasure) {
                    addProductToList(product, item, index, props.result, props.parameter.checkInventory);
                }
            } else {
                props.result.rowErrors.push(mapRowError(index, error, item.name, product));
            }
        } else {
            props.result.rowErrors.push(
                mapRowError(index, StoreUploadError.NotFound /* Store Custom Code */, item.name, {
                    qtyOrdered: item.qtyOrdered,
                    unitOfMeasureDisplay: item.unitOfMeasure,
                } as ProductDto),
            );
        }
    }
};

const validateProduct = (product: ProductDto, checkInventory: boolean) => {
    if (product.canConfigure || (product.isConfigured && !product.isFixedConfiguration)) {
        return StoreUploadError.ConfigurableProduct; // Store Custom Code
    }

    if (product.isStyleProductParent) {
        return StoreUploadError.StyledProduct; // Store Custom Code
    }

    if (checkInventory && product.qtyOnHand === 0 && product.trackInventory && !product.canBackOrder) {
        return StoreUploadError.OutOfStock; // Store Custom Code
    }

    if (!product.canAddToCart) {
        return StoreUploadError.Unavailable; // Store Custom Code
    }

    /* Store Custom Code - Start */
    if (
        (product as any).properties.isScratchAndDent &&
        (product as any).properties!.isScratchAndDent.toLowerCase() === "true"
    ) {
        return StoreUploadError.SnDProduct;
    }
    /* Store Custom Code - End */

    return StoreUploadError.None;
};

const setProductUnitOfMeasure = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    rowErrors: StoreOrderUploadRowError[], // Store Custom Code
) => {
    const uoms = getProductUnitOfMeasures(product, item);
    if (uoms.length > 0) {
        const um = uoms[0];
        product.selectedUnitOfMeasure = um.unitOfMeasure;
        product.selectedUnitOfMeasureDisplay = um.unitOfMeasureDisplay;
        product.unitOfMeasure = um.unitOfMeasure;
        product.unitOfMeasureDisplay = um.unitOfMeasureDisplay;

        return false;
    }

    if (item.unitOfMeasure) {
        const errorProduct = mapRowError(index, StoreUploadError.InvalidUnit, item.name, product); // Store Custom Code
        errorProduct.umRequested = item.unitOfMeasure;
        rowErrors.push(errorProduct);

        return true;
    }

    return false;
};

const addProductToList = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    result: StoreBatchLoadProductsResult, // Store Custom Code
    checkInventory: boolean,
) => {
    const baseUnitOfMeasure = getBaseUnitOfMeasure(product);
    const currentUnitOfMeasure = getCurrentUnitOfMeasure(product);
    if (
        checkInventory &&
        product.trackInventory &&
        !product.canBackOrder &&
        !product.quoteRequired &&
        baseUnitOfMeasure &&
        currentUnitOfMeasure &&
        product.qtyOrdered * baseUnitOfMeasure.qtyPerBaseUnitOfMeasure >
            product.qtyOnHand * currentUnitOfMeasure.qtyPerBaseUnitOfMeasure
    ) {
        const rowError = mapRowError(index, StoreUploadError.NotEnough, item.name, product); // Store Custom Code
        result.rowErrors.push(rowError);
    }

    result.products.push(product);
};

const getBaseUnitOfMeasure = (product: ProductDto) => {
    const defaultUnitOfMeasure = getDefaultProductUnitOfMeasureDto();
    let baseUnitOfMeasure = product.productUnitOfMeasures?.filter(u => u.isDefault)[0];
    if (!baseUnitOfMeasure) {
        baseUnitOfMeasure = defaultUnitOfMeasure;
    }

    return baseUnitOfMeasure;
};

const getCurrentUnitOfMeasure = (product: ProductDto) => {
    const defaultUnitOfMeasure = getDefaultProductUnitOfMeasureDto();
    let currentUnitOfMeasure = product.productUnitOfMeasures?.filter(u => u.unitOfMeasure === product.unitOfMeasure)[0];
    if (!currentUnitOfMeasure) {
        currentUnitOfMeasure = defaultUnitOfMeasure;
    }

    return currentUnitOfMeasure;
};

const getDefaultProductUnitOfMeasureDto = () => {
    return {
        productUnitOfMeasureId: "",
        unitOfMeasure: "",
        unitOfMeasureDisplay: "",
        description: "",
        qtyPerBaseUnitOfMeasure: 1,
        roundingRule: "",
        isDefault: false,
        availability: null,
    };
};

const mapRowError = (
    index: number,
    error: StoreUploadError /* Store Custom Code */,
    name: string,
    product: ProductDto,
): StoreOrderUploadRowError => {
    return {
        index,
        error,
        name,
        qtyRequested: product.qtyOrdered,
        umRequested: product.unitOfMeasureDisplay,
        qtyOnHand: product.qtyOnHand,
    };
};

const getProductUnitOfMeasures = (product: ProductDto, item: UploadedItem) => {
    const lowerCaseItemUm = item.unitOfMeasure ? item.unitOfMeasure.toLowerCase() : "";

    return lowerCaseItemUm && product.productUnitOfMeasures
        ? product.productUnitOfMeasures.filter(
              u =>
                  u.unitOfMeasure.toLowerCase() === lowerCaseItemUm ||
                  u.unitOfMeasureDisplay.toLowerCase() === lowerCaseItemUm ||
                  u.description.toLowerCase() === lowerCaseItemUm,
          )
        : [];
};

replaceInChain(chain, ProcessProducts, StoreProcessProducts);
